import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import './ExplorePlans.css';
import { pushEvent, EVENTS } from '../../utils/dataLayer';

// Dedicated landing page after a confirmed payment.
// Exists as a real URL (/payment-success) so ad platforms can fire a
// destinuation-URL conversion — the old flow redirected straight to '/',
// which is indistinguishable from a normal homepage visit.
// Holds the user here for FORWARD_DELAY_MS to give the tags time to fire,
// then completes the login.dastaan.info -> dastaan.info session handoff.

const FORWARD_DELAY_MS = 4000;

const isLoginDomain = () => {
    const hostname = window.location.hostname;
    const loginDomain = process.env.REACT_APP_LOGIN_DOMAIN || 'login.dastaan.info';
    return hostname === loginDomain || process.env.REACT_APP_DOMAIN === 'login';
};

const MAIN_DOMAIN_URL = process.env.REACT_APP_MAIN_DOMAIN_URL || `https://${process.env.REACT_APP_MAIN_DOMAIN || 'dastaan.info'}/`;

const PaymentSuccess = () => {
    const params = new URLSearchParams(window.location.search);
    const txnid = params.get('txnid') || '';
    const amount = params.get('amount') || localStorage.getItem('dastaan__plan_amnt') || '';
    const [seconds, setSeconds] = useState(Math.ceil(FORWARD_DELAY_MS / 1000));

    // Auth data is read from localStorage rather than the URL — this page is
    // served from the same origin that stored it, so the token never has to
    // travel through the success URL.
    const buildHandoffUrl = () => {
        const authParams = new URLSearchParams({
            auth_token: localStorage.getItem('dastaan__token') || '',
            auth_user_id: localStorage.getItem('dastaan__user_id') || '',
            auth_phone: localStorage.getItem('dastaan__phone') || '',
            auth_name: localStorage.getItem('dastaan__name') || '',
            auth_user_image: localStorage.getItem('dastaan__user_image') || '',
            auth_plan_id: localStorage.getItem('dastaan__plan_id') || '',
            auth_plan_amnt: localStorage.getItem('dastaan__plan_amnt') || '',
            auth_plan_active: localStorage.getItem('dastaan__plan_active') || '',
        });
        return `${MAIN_DOMAIN_URL}?${authParams.toString()}`;
    };

    const goForward = () => {
        window.location.href = isLoginDomain() ? buildHandoffUrl() : '/';
    };

    // Announce the purchase to GTM so the Google Ads conversion tag can fire
    // on a Custom Event trigger as well as on the URL trigger.
    // Top-level transaction_id/value/currency are kept for the existing Ads tag;
    // the ecommerce object is the GA4 purchase shape.
    useEffect(() => {
        const value = amount ? Number(amount) : undefined;
        const planId = localStorage.getItem('dastaan__plan_id');
        pushEvent(EVENTS.PURCHASE, {
            transaction_id: txnid,
            value,
            currency: 'INR',
            ecommerce: {
                transaction_id: txnid,
                value,
                currency: 'INR',
                items: [{ item_id: planId ? String(planId) : undefined, price: value, item_category: 'subscription', quantity: 1 }],
            },
        });
    }, [txnid, amount]);

    useEffect(() => {
        const forwardTimer = setTimeout(goForward, FORWARD_DELAY_MS);
        const tick = setInterval(() => setSeconds(s => (s > 0 ? s - 1 : 0)), 1000);
        return () => {
            clearTimeout(forwardTimer);
            clearInterval(tick);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Container
            style={{
                minHeight: '100vh',
                width: '100%',
                backgroundImage: `url(${process.env.PUBLIC_URL}/images/${
                    window.location.hostname === 'login.dastaan.info'
                        ? 'modal-bg-login.jpg'
                        : 'modal-bg.jpg'
                })`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
            }}
        >
            <div style={{ maxWidth: '420px', padding: '24px', color: '#fff' }}>
                <div
                    style={{
                        width: '84px',
                        height: '84px',
                        margin: '0 auto 24px',
                        borderRadius: '50%',
                        background: 'rgba(40, 167, 69, 0.15)',
                        border: '2px solid #28a745',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '42px',
                        color: '#28a745',
                        lineHeight: 1,
                    }}
                >
                    &#10003;
                </div>

                <h2 style={{ fontWeight: 700, marginBottom: '12px' }}>Payment Successful!</h2>
                <p style={{ color: 'rgba(255,255,255,0.75)', marginBottom: '20px' }}>
                    Your subscription is active. Enjoy unlimited access to Dastaan.
                </p>

                {(amount || txnid) && (
                    <div
                        style={{
                            background: 'rgba(255,255,255,0.07)',
                            borderRadius: '10px',
                            padding: '14px 18px',
                            marginBottom: '24px',
                            fontSize: '14px',
                            textAlign: 'left',
                        }}
                    >
                        {amount && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: txnid ? '8px' : 0 }}>
                                <span style={{ color: 'rgba(255,255,255,0.6)' }}>Amount paid</span>
                                <strong>&#8377; {amount}</strong>
                            </div>
                        )}
                        {txnid && (
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: 'rgba(255,255,255,0.6)' }}>Transaction ID</span>
                                <span style={{ wordBreak: 'break-all' }}>{txnid}</span>
                            </div>
                        )}
                    </div>
                )}

                <button
                    className="pay-now"
                    onClick={goForward}
                    style={{ width: '100%' }}
                >
                    Start Listening
                </button>

                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginTop: '14px' }}>
                    Redirecting you in {seconds}s&hellip;
                </p>
            </div>
        </Container>
    );
};

export default PaymentSuccess;
